import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FiChevronDown, FiEdit2, FiX } from "react-icons/fi";
import api from "../../services/api";
import { PROPERTY_TYPES } from "../PropertiesList/components/TitleSearch/SearchFilter/constants";
import type { PropertyAlertListItem } from "./PropertyAlertsSection";
import {
  ModalOverlay,
  ModalPanel,
  ModalHeader,
  ModalHeaderLeft,
  ModalHeaderIcon,
  ModalTitle,
  ModalSubtitle,
  ModalClose,
  ModalBody,
  ModalFooter,
  FieldGroup,
  FieldLabel,
  FieldHint,
  TextInput,
  KindToggleRow,
  KindOption,
  RowTwoCols,
  NotifySection,
  NotifySectionTitle,
  CheckboxRow,
  CheckboxInput,
  CheckboxText,
  SelectWrapper,
  SelectNative,
  SelectChevron,
  FieldError,
  ErrorText,
  FooterButtonSecondary,
  FooterButtonPrimary,
} from "../PropertiesList/components/PropertyRadarCTA/styles";

type Segment = "residential" | "commercial";

type FieldErrors = Record<string, string>;

function pickFirstApiErrors(
  raw: Record<string, string[] | string> | undefined
): FieldErrors {
  if (!raw) return {};
  const out: FieldErrors = {};
  Object.entries(raw).forEach(([key, val]) => {
    if (Array.isArray(val) && val[0]) out[key] = val[0];
    else if (typeof val === "string" && val) out[key] = val;
  });
  return out;
}

interface Props {
  open: boolean;
  onClose: () => void;
  alert: PropertyAlertListItem | null;
  accessToken: string;
  onSaved: () => void;
}

export default function PropertyAlertEditModal({
  open,
  onClose,
  alert,
  accessToken,
  onSaved,
}: Props) {
  const [city, setCity] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [maxRent, setMaxRent] = useState("");
  const [minRooms, setMinRooms] = useState("");
  const [propertyTypeId, setPropertyTypeId] = useState("");
  const [segment, setSegment] = useState<Segment>("residential");
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifyWhatsapp, setNotifyWhatsapp] = useState(false);
  const [whatsappOptIn, setWhatsappOptIn] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [legacyWhatsappOptInAt, setLegacyWhatsappOptInAt] = useState<
    string | null
  >(null);

  const resetFromAlert = useCallback((a: PropertyAlertListItem) => {
    setCity(a.city?.trim() || "");
    setNeighborhood(a.neighborhood?.trim() || "");
    const rentNum =
      typeof a.max_rent === "number"
        ? a.max_rent
        : parseFloat(String(a.max_rent ?? ""));
    setMaxRent(Number.isFinite(rentNum) ? String(rentNum) : "");
    setMinRooms(
      a.min_rooms !== null && a.min_rooms !== undefined
        ? String(a.min_rooms)
        : ""
    );
    setPropertyTypeId(
      a.property_type_id !== null && a.property_type_id !== undefined
        ? String(a.property_type_id)
        : ""
    );
    setSegment(a.segment === "commercial" ? "commercial" : "residential");
    setNotifyEmail(a.notify_email !== false);
    setNotifyWhatsapp(!!a.notify_whatsapp);
    setWhatsappOptIn(!!a.whatsapp_opt_in);
    setIsActive(a.is_active !== false);
    setLegacyWhatsappOptInAt(a.whatsapp_opt_in_at ?? null);
    setFieldErrors({});
  }, []);

  useEffect(() => {
    if (open && alert) {
      resetFromAlert(alert);
    }
  }, [open, alert, resetFromAlert]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const clearFieldError = useCallback((key: string) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const validate = (): FieldErrors => {
    const e: FieldErrors = {};
    if (!neighborhood.trim() && !city.trim()) {
      e.neighborhood =
        "Informe bairro ou cidade (pelo menos um dos dois).";
    }
    const maxNum = Number(maxRent);
    if (maxRent.trim() === "" || Number.isNaN(maxNum) || maxNum < 0) {
      e.max_rent = "Valor máximo inválido.";
    }
    const roomsNum = Number(minRooms);
    if (
      minRooms.trim() === "" ||
      Number.isNaN(roomsNum) ||
      roomsNum < 0 ||
      !Number.isInteger(roomsNum)
    ) {
      e.min_rooms = "Mínimo de quartos deve ser um inteiro ≥ 0.";
    }
    const hadLegacyOptIn = Boolean(legacyWhatsappOptInAt);
    if (notifyWhatsapp && !whatsappOptIn && !hadLegacyOptIn) {
      e.whatsapp_opt_in =
        "Marque o opt-in do WhatsApp ou desative notificações por WhatsApp.";
    }
    return e;
  };

  const buildPutBody = (): Record<string, unknown> => {
    const body: Record<string, unknown> = {
      max_rent: Number(maxRent),
      min_rooms: Number(minRooms),
      segment,
      notify_email: notifyEmail,
      notify_whatsapp: notifyWhatsapp,
      whatsapp_opt_in: whatsappOptIn,
      is_active: isActive,
    };
    const cityTrim = city.trim();
    if (cityTrim) body.city = cityTrim;
    else body.city = null;

    const nh = neighborhood.trim();
    if (nh) body.neighborhood = nh;
    else body.neighborhood = null;

    if (propertyTypeId) {
      const id = Number(propertyTypeId);
      if (!Number.isNaN(id)) body.property_type_id = id;
    } else {
      body.property_type_id = null;
    }

    return body;
  };

  const handleSubmit = async () => {
    if (!alert) return;
    setFieldErrors({});
    const local = validate();
    if (Object.keys(local).length > 0) {
      setFieldErrors(local);
      return;
    }

    setSubmitting(true);
    try {
      api.defaults.headers.authorization = `Bearer ${accessToken}`;
      await api.put(
        `/api/client/tenant/property-alerts/${alert.id}`,
        buildPutBody()
      );
      onSaved();
      onClose();
    } catch (err: unknown) {
      const ax = err as {
        response?: { data?: { message?: string; errors?: Record<string, string[]> } };
      };
      const apiErrors = pickFirstApiErrors(ax.response?.data?.errors);
      if (Object.keys(apiErrors).length > 0) {
        setFieldErrors(apiErrors);
      } else {
        setFieldErrors({
          _general:
            ax.response?.data?.message ||
            "Não foi possível atualizar o alerta.",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !alert) return null;

  const showWhatsappOptInRow = notifyWhatsapp;
  const hadLegacyOptIn = Boolean(legacyWhatsappOptInAt);

  const modal = (
    <ModalOverlay
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <ModalPanel
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-alert-title"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalHeaderLeft>
            <ModalHeaderIcon aria-hidden>
              <FiEdit2 size={22} />
            </ModalHeaderIcon>
            <div>
              <ModalTitle id="edit-alert-title">Editar alerta</ModalTitle>
              <ModalSubtitle>
                Ajuste critérios, canais e status do alerta.
              </ModalSubtitle>
            </div>
          </ModalHeaderLeft>
          <ModalClose type="button" onClick={onClose} aria-label="Fechar">
            <FiX size={22} />
          </ModalClose>
        </ModalHeader>

        <ModalBody>
          <FieldGroup>
            <FieldLabel htmlFor="edit-alert-city">Cidade</FieldLabel>
            <TextInput
              id="edit-alert-city"
              value={city}
              onChange={(e) => {
                setCity(e.target.value);
                clearFieldError("city");
              }}
              $invalid={!!fieldErrors.city}
            />
            {fieldErrors.city && (
              <FieldError role="alert">{fieldErrors.city}</FieldError>
            )}
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor="edit-alert-neighborhood">
              Bairro <FieldHint>(obrigatório se cidade vazia)</FieldHint>
            </FieldLabel>
            <TextInput
              id="edit-alert-neighborhood"
              value={neighborhood}
              onChange={(e) => {
                setNeighborhood(e.target.value);
                clearFieldError("neighborhood");
              }}
              $invalid={!!fieldErrors.neighborhood}
            />
            {fieldErrors.neighborhood && (
              <FieldError role="alert">{fieldErrors.neighborhood}</FieldError>
            )}
          </FieldGroup>

          <RowTwoCols>
            <FieldGroup>
              <FieldLabel htmlFor="edit-alert-max">Valor máx. (R$)</FieldLabel>
              <TextInput
                id="edit-alert-max"
                type="number"
                min={0}
                step={100}
                value={maxRent}
                onChange={(e) => {
                  setMaxRent(e.target.value);
                  clearFieldError("max_rent");
                }}
                $invalid={!!fieldErrors.max_rent}
              />
              {fieldErrors.max_rent && (
                <FieldError role="alert">{fieldErrors.max_rent}</FieldError>
              )}
            </FieldGroup>
            <FieldGroup>
              <FieldLabel htmlFor="edit-alert-rooms">Mín. quartos</FieldLabel>
              <TextInput
                id="edit-alert-rooms"
                type="number"
                min={0}
                step={1}
                value={minRooms}
                onChange={(e) => {
                  setMinRooms(e.target.value);
                  clearFieldError("min_rooms");
                }}
                $invalid={!!fieldErrors.min_rooms}
              />
              {fieldErrors.min_rooms && (
                <FieldError role="alert">{fieldErrors.min_rooms}</FieldError>
              )}
            </FieldGroup>
          </RowTwoCols>

          <FieldGroup>
            <FieldLabel htmlFor="edit-alert-type">Tipo (opcional)</FieldLabel>
            <SelectWrapper>
              <SelectNative
                id="edit-alert-type"
                value={propertyTypeId}
                onChange={(e) => {
                  setPropertyTypeId(e.target.value);
                  clearFieldError("property_type_id");
                }}
                $invalid={!!fieldErrors.property_type_id}
              >
                <option value="">Não especificar</option>
                {PROPERTY_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </SelectNative>
              <SelectChevron aria-hidden>
                <FiChevronDown size={20} strokeWidth={2.25} />
              </SelectChevron>
            </SelectWrapper>
            {fieldErrors.property_type_id && (
              <FieldError role="alert">
                {fieldErrors.property_type_id}
              </FieldError>
            )}
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>Segmento</FieldLabel>
            <KindToggleRow role="group" aria-label="Segmento">
              <KindOption
                type="button"
                $active={segment === "residential"}
                onClick={() => {
                  setSegment("residential");
                  clearFieldError("segment");
                }}
              >
                Residencial
              </KindOption>
              <KindOption
                type="button"
                $active={segment === "commercial"}
                onClick={() => {
                  setSegment("commercial");
                  clearFieldError("segment");
                }}
              >
                Comercial
              </KindOption>
            </KindToggleRow>
            {fieldErrors.segment && (
              <FieldError role="alert">{fieldErrors.segment}</FieldError>
            )}
          </FieldGroup>

          <NotifySection>
            <NotifySectionTitle>Notificações</NotifySectionTitle>
            <CheckboxRow>
              <CheckboxInput
                type="checkbox"
                checked={notifyEmail}
                onChange={(e) => {
                  setNotifyEmail(e.target.checked);
                  clearFieldError("notify_email");
                }}
              />
              <CheckboxText>Avisar por e-mail</CheckboxText>
            </CheckboxRow>
            <CheckboxRow>
              <CheckboxInput
                type="checkbox"
                checked={notifyWhatsapp}
                onChange={(e) => {
                  setNotifyWhatsapp(e.target.checked);
                  if (!e.target.checked) setWhatsappOptIn(false);
                  clearFieldError("notify_whatsapp");
                  clearFieldError("whatsapp_opt_in");
                }}
              />
              <CheckboxText>Avisar por WhatsApp</CheckboxText>
            </CheckboxRow>
            {hadLegacyOptIn && (
              <p
                style={{
                  margin: "0 0 8px 0",
                  paddingLeft: 28,
                  fontSize: 13,
                  color: "#666",
                  lineHeight: 1.4,
                }}
              >
                Este alerta já possui opt-in de WhatsApp registrado
                anteriormente.
              </p>
            )}
            {showWhatsappOptInRow && (
              <CheckboxRow>
                <CheckboxInput
                  type="checkbox"
                  checked={whatsappOptIn}
                  onChange={(e) => {
                    setWhatsappOptIn(e.target.checked);
                    clearFieldError("whatsapp_opt_in");
                  }}
                />
                <CheckboxText>
                  Confirmo opt-in para WhatsApp (obrigatório se não houver
                  registro anterior)
                </CheckboxText>
              </CheckboxRow>
            )}
            {fieldErrors.whatsapp_opt_in && (
              <FieldError role="alert">{fieldErrors.whatsapp_opt_in}</FieldError>
            )}
          </NotifySection>

          <FieldGroup>
            <CheckboxRow>
              <CheckboxInput
                type="checkbox"
                checked={isActive}
                onChange={(e) => {
                  setIsActive(e.target.checked);
                  clearFieldError("is_active");
                }}
              />
              <CheckboxText>Alerta ativo</CheckboxText>
            </CheckboxRow>
            {fieldErrors.is_active && (
              <FieldError role="alert">{fieldErrors.is_active}</FieldError>
            )}
          </FieldGroup>

          {fieldErrors._general && (
            <ErrorText role="alert">{fieldErrors._general}</ErrorText>
          )}
        </ModalBody>

        <ModalFooter>
          <FooterButtonSecondary type="button" onClick={onClose}>
            Cancelar
          </FooterButtonSecondary>
          <FooterButtonPrimary
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Salvando…" : "Salvar alterações"}
          </FooterButtonPrimary>
        </ModalFooter>
      </ModalPanel>
    </ModalOverlay>
  );

  return createPortal(modal, document.body);
}
